import { useState, useCallback, useRef } from "react";
import extraService from "../services/extrasService";

/**
 * Trae el catálogo de cámaras (servicio ArcGIS CISE_Camaras, vía credenciales
 * de MetroGIS) y permite filtrarlo por estación.
 *
 * El cruce estación → cámaras se hace por el código de 3 letras:
 * `estacion.attributes.alias_estacion` === `camara.attributes.estacion`
 * (p. ej. "JOS" → Estación San José).
 *
 * El catálogo se pide una sola vez y se cachea, porque el flujo de la vista
 * consulta cámaras cada vez que el operador hace click en una estación.
 */
export default function useCamarasEstacion() {
  const [camaras, setCamaras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // La caché vive en un ref, no en el estado: el handler de click del mapa se
  // registra una sola vez y su closure conservaría para siempre el `camaras`
  // del primer render (vacío), devolviendo 0 cámaras a partir de la segunda
  // estación consultada.
  const cacheRef = useRef(null);
  const enVuelo = useRef(null);

  const cargarCamaras = useCallback(async () => {
    if (cacheRef.current) return cacheRef.current;
    // Evita ráfagas de peticiones si el operador hace varios clicks seguidos
    // antes de que termine la primera.
    if (enVuelo.current) return enVuelo.current;

    setLoading(true);
    setError(null);

    const promesa = (async () => {
      try {
        const data = await extraService.consultarCamaras();
        const lista = normalizarCamaras(data?.camaras || []);
        cacheRef.current = lista;
        setCamaras(lista);
        return lista;
      } catch (err) {
        console.error("Error al consultar cámaras:", err);
        setError("No se logró consultar el catálogo de cámaras.");
        return [];
      } finally {
        setLoading(false);
        enVuelo.current = null;
      }
    })();

    enVuelo.current = promesa;
    return promesa;
  }, []);

  const camarasDeEstacion = useCallback((aliasEstacion, lista) => {
    const fuente = lista || cacheRef.current || [];
    if (!aliasEstacion) return [];
    const alias = String(aliasEstacion).trim().toUpperCase();
    return fuente.filter((c) => c.estacion === alias);
  }, []);

  return { camaras, loading, error, cargarCamaras, camarasDeEstacion };
}

/**
 * Aplana la respuesta del servicio a un shape estable.
 *
 * Se usan `longitud`/`latitud` (WGS84) y se ignoran `x_ctm12`/`y_ctm12`, que
 * vienen proyectadas y no sirven como longitude/latitude para ArcGIS.
 */
function normalizarCamaras(items) {
  return items
    .map((item) => {
      const a = item?.attributes || {};
      const lon = Number(a.longitud);
      const lat = Number(a.latitud);
      return {
        deviceId: a.deviceid || null,
        nombre: a.nombre || "Sin nombre",
        estacion: a.estacion ? String(a.estacion).trim().toUpperCase() : null,
        equipo: a.equipo || null,
        tecnologia: a.tecnologia || null,
        linea: a.linea || null,
        lon,
        lat,
      };
    })
    .filter(
      (c) =>
        c.deviceId &&
        c.estacion &&
        Number.isFinite(c.lon) &&
        Number.isFinite(c.lat)
    );
}
